/**
 * Cart service
 * Handles all cart operations (add, remove, update, clear).
 * Supports both logged-in users (server cart) and guests (localStorage guestCart).
 */
import APIClient from '../apiClient';
import { trackEvent } from '../analytics/analyticsService';

const GUEST_CART_STORAGE_KEY = 'guestCart';

/**
 * Fired on window after any cart change made through this service, so the
 * cart badges (hooks/useCartCount) update without polling.
 */
export const CART_CHANGED_EVENT = 'dialine:cart-changed';
const notifyCartChanged = () => {
  try {
    window.dispatchEvent(new Event(CART_CHANGED_EVENT));
  } catch (e) {
    // Non-browser environment; nothing to notify.
  }
};

function getGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function setGuestCart(cart) {
  try {
    localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(Array.isArray(cart) ? cart : []));
  } catch (e) {
  }
}

function isLoggedIn() {
  return localStorage.getItem('loggedIn') === '1';
}

// Get all items in cart (logged-in: from API; guest: from localStorage)
export const getAllCartItems = async () => {
  if (!isLoggedIn()) {
    return {
      success: true,
      data: { cart: getGuestCart() }
    };
  }
  try {
    const response = await APIClient.get('/products/cart/getAllItems');

    return {
      success: true,
      data: {
        cart: response.data?.data?.cart || []
      }
    };
  } catch (error) {
    if (error.response?.status === 400) {
      return { success: true, data: { cart: [] } };
    }
    if (error.response?.status === 401) {
      throw error;
    }
    return { success: true, data: { cart: [] } };
  }
};

// Add to cart: same endpoint for logged-in and guest. Guest sends guestCart and persists response.
export const addToCart = async (pharmacode, quantity = 1) => {
  const loggedIn = isLoggedIn();
  const body = loggedIn
    ? { quantity }
    : { quantity, guestCart: getGuestCart() };

  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await APIClient.post(
      `/products/cart/${pharmacode}/addToCart`,
      body,
      config
    );

    const data = response.data;
    if (!loggedIn && data.cart) {
      setGuestCart(data.cart);
    }
    trackEvent('add_to_cart', { pharmacode, quantity });
    notifyCartChanged();
    return data;
  } catch (error) {
    throw error;
  }
};

// Remove item: API for logged-in; update localStorage for guest (backend does not persist guest cart)
export const removeFromCart = async (productId) => {
  if (!isLoggedIn()) {
    const cart = getGuestCart().filter(
      (item) => String(item._id) !== String(productId) && Number(item.pharmacode) !== Number(productId)
    );
    setGuestCart(cart);
    notifyCartChanged();
    return { success: true, message: 'Item removed', cart };
  }
  try {
    const response = await APIClient.post(
      `/products/cart/${productId}/removeFromCart`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    notifyCartChanged();
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update quantity: same addToCart endpoint for both; guest sends guestCart and persists response
export const updateCartQuantity = async (pharmacode, quantity) => {
  const loggedIn = isLoggedIn();
  const body = loggedIn
    ? { quantity }
    : { quantity, guestCart: getGuestCart() };

  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await APIClient.post(
      `/products/cart/${pharmacode}/addToCart`,
      body,
      config
    );
    const data = response.data;
    if (!loggedIn && data.cart) {
      setGuestCart(data.cart);
    }
    notifyCartChanged();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Merge the guest cart into the freshly-logged-in user's server cart.
 *
 * Call this immediately after a successful login, once the token is stored.
 * Previously the guest cart was simply abandoned: everything a visitor added
 * before signing in silently vanished the moment they logged in.
 *
 * Quantities are summed, not overwritten. The backend's addToCart *sets*
 * quantity rather than incrementing it, so sending the guest quantity directly
 * would wipe out whatever the user already had saved on the server. We read the
 * server cart first and send the combined total instead.
 *
 * Requests are issued one at a time on purpose. Each one does findById → mutate
 * → save on the same user document, so firing them in parallel would let the
 * later save overwrite the earlier one and drop items.
 *
 * Never throws: a failed merge must not break logging in. Items that fail stay
 * in the guest cart rather than being lost, so a later login can retry them.
 *
 * @returns {Promise<{merged: number, failed: number}>}
 */
export const mergeGuestCartIntoAccount = async () => {
  if (!isLoggedIn()) return { merged: 0, failed: 0 };

  const guestCart = getGuestCart();
  if (!guestCart.length) return { merged: 0, failed: 0 };

  // Read the server cart directly rather than via getAllCartItems: that helper
  // converts request failures into an empty cart with success: true, which is
  // indistinguishable from a genuinely empty cart. Treating a failed read as
  // "empty" would make us send the raw guest quantity and wipe out whatever the
  // user actually had saved — the exact overwrite this merge exists to avoid.
  let serverCart = [];
  try {
    const response = await APIClient.get('/products/cart/getAllItems', {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    serverCart = response.data?.data?.cart || [];
  } catch (e) {
    // Cannot see the server cart, so no safe total can be computed. Leave the
    // guest cart untouched; a later login will retry it.
    return { merged: 0, failed: guestCart.length };
  }

  const serverQuantityByPharmacode = new Map(
    serverCart.map((item) => [Number(item.pharmacode), Number(item.quantity) || 0])
  );

  const unmerged = [];
  let merged = 0;

  for (const item of guestCart) {
    const pharmacode = Number(item.pharmacode);
    const guestQuantity = Number(item.quantity) || 0;
    if (!pharmacode || guestQuantity <= 0) continue;

    const combined = (serverQuantityByPharmacode.get(pharmacode) || 0) + guestQuantity;

    try {
      await updateCartQuantity(pharmacode, combined);
      merged += 1;
    } catch (e) {
      // Product may have gone out of catalogue, become Rx-only, or the request
      // may have failed transiently. Keep it so the user does not lose it.
      unmerged.push(item);
    }
  }

  setGuestCart(unmerged);
  notifyCartChanged();

  return { merged, failed: unmerged.length };
};

// Clear cart: API for logged-in; clear localStorage for guest
export const clearCart = async () => {
  if (!isLoggedIn()) {
    setGuestCart([]);
    notifyCartChanged();
    return { success: true, message: 'Cart cleared', cart: [] };
  }
  try {
    const response = await APIClient.post(
      '/products/cart/clear',
      {},
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    notifyCartChanged();
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Cart count: API for logged-in; length of guest cart from storage for guest
export const getCartCount = async () => {
  if (!isLoggedIn()) {
    const cart = getGuestCart();
    const count = cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return { data: { count } };
  }
  try {
    const response = await APIClient.get('/products/cart/count', {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

